import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../services/api'

export default function Initiatives() {
  const [initiatives, setInitiatives] = useState([])
  const [insightsByInitiative, setInsightsByInitiative] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    api
      .get('/api/v1/initiatives')
      .then(async (data) => {
        if (cancelled) return
        setInitiatives(data)

        // Fetch the latest insight per initiative in parallel. Each call is
        // cheap and we'd rather eager-load than block on navigation.
        const results = await Promise.all(
          data.map((init) =>
            api
              .get(`/api/v1/insights/by_initiative/${init.id}`)
              .then((payload) => [ init.id, payload.insights?.[0] || null ])
              .catch(() => [ init.id, null ])
          )
        )
        if (cancelled) return
        setInsightsByInitiative(Object.fromEntries(results))
      })
      .catch((err) => setError(err.message))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Initiatives</h2>
          <p className="mt-1 text-sm text-slate-500">
            Cross-agency programs and the knowledge each agency has shared about them.
          </p>
        </div>
        <Link
          to="/knowledge/themes"
          className="text-sm font-medium text-accent-600 hover:text-accent-500"
        >
          Cross-cutting themes →
        </Link>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {error && <p className="text-sm text-rose-600">{error}</p>}

      {!loading && initiatives.length === 0 && (
        <p className="text-sm text-slate-500">No initiatives yet. Ask an admin to create one.</p>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {initiatives.map((init) => {
          const insight = insightsByInitiative[init.id]
          return (
            <Link
              key={init.id}
              to={`/initiatives/${init.slug}`}
              className="block rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover:border-accent-300 hover:shadow transition"
            >
              <h3 className="text-lg font-semibold text-slate-900">{init.name}</h3>
              {init.description && (
                <p className="mt-1 text-sm text-slate-500 line-clamp-2">{init.description}</p>
              )}

              {insight ? (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="text-xs font-semibold uppercase tracking-wide text-accent-700 mb-2">
                    AI summary
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap line-clamp-6">
                    {insight.content}
                  </p>
                </div>
              ) : (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-400">
                    No AI summary yet. Admins can generate insights via the API.
                  </p>
                </div>
              )}
            </Link>
          )
        })}
      </div>
    </div>
  )
}
